import {Modal} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Collapse} from 'src/design/atoms/Collapse';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {IconAlertCircle, IconChevronDown, IconCheck} from '@tabler/icons-react';
import React, {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';

export interface EntityActiveStatusPanelProps {
  /** e.g. "Client" or "Site" — drives the copy. */
  entityLabel: string;
  isActive: boolean;
  loading: boolean;
  /** Performs the active-status mutation; the caller owns the request/cache. */
  onToggle: () => Promise<unknown>;
}

/**
 * Enable/disable panel, cloned from Vendor's `VendorActiveStatusTab` (same
 * confirm-modal + audited-toggle UX) but generalized across entity labels
 * and hosted inline on the (non-tabbed) Client/Site view pages rather than
 * as a tab panel.
 */
export const EntityActiveStatusPanel: React.FC<EntityActiveStatusPanelProps> = ({
  entityLabel,
  isActive,
  loading,
  onToggle,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, {toggle}] = useDisclosure(false);

  const handleToggle = async () => {
    setError(null);
    try {
      await onToggle();
      setModalOpen(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update status.');
    }
  };

  return (
    <SectionCard
      title="Active Status"
      onHeaderClick={toggle}
      rightSection={
        <IconChevronDown
          size={18}
          style={{
            transform: opened ? 'rotate(180deg)' : undefined,
            transition: 'transform 150ms',
          }}
        />
      }>
      <Collapse in={opened}>
        <Box p="md">
          <Alert
            icon={<IconAlertCircle />}
            color="red"
            title={isActive ? `Disable ${entityLabel}` : `Enable ${entityLabel}`}>
            <Text size="sm" mb="md">
              {isActive
                ? `Disabling this ${entityLabel.toLowerCase()} will mark it inactive and hide it from selection elsewhere in the system.`
                : `Enabling this ${entityLabel.toLowerCase()} will make it active and available for selection elsewhere in the system.`}
            </Text>
            <Text size="sm" mb="md">
              An audit record will be created for this action.
            </Text>
            <Button
              size="sm"
              color={isActive ? 'red' : 'green'}
              leftSection={isActive ? <IconAlertCircle /> : <IconCheck />}
              onClick={() => setModalOpen(true)}
              loading={loading}>
              {isActive ? `Disable ${entityLabel}` : `Enable ${entityLabel}`}
            </Button>
          </Alert>
        </Box>
      </Collapse>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          isActive
            ? `Confirm Disable ${entityLabel}`
            : `Confirm Enable ${entityLabel}`
        }
        centered>
        <Text mb="md">
          {isActive
            ? `Are you sure you want to disable this ${entityLabel.toLowerCase()}? It will be marked inactive and hidden from selection elsewhere in the system.`
            : `Are you sure you want to enable this ${entityLabel.toLowerCase()}? It will become active and available for selection elsewhere in the system.`}
        </Text>
        {error && (
          <Alert color="red" mb="md">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            size="sm"
            color="dark"
            variant="subtle"
            onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            color={isActive ? 'red' : 'green'}
            onClick={handleToggle}
            loading={loading}>
            {isActive
              ? `I am sure, disable ${entityLabel.toLowerCase()}`
              : `I am sure, enable ${entityLabel.toLowerCase()}`}
          </Button>
        </Group>
      </Modal>
    </SectionCard>
  );
};
